import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeKatex from "rehype-katex";
import remarkSpoiler from "@/lib/remarkSpoiler";
import remarkMention from "@/lib/remarkMention";
import preprocessMath from "@/lib/mathDelimiters";
import { htmlToMarkdown, isHtmlContent } from "@/lib/markdown";
import { cn } from "@/lib/utils";
import { LINK_CLASS } from "@/config/constants";
import UserProfileDialog from "./UserProfileDialog";
import type { Components } from "react-markdown";
import type { PluggableList } from "unified";

// Allowlist schema: GitHub's default + span attributes needed by remark plugins.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span || []),
      ["className", "mention", "spoiler"],
      "dataUsername",
      ["dataSpoiler", "true"],
    ],
  },
};

const REHYPE_PLUGINS: PluggableList = [
  rehypeRaw,
  [rehypeSanitize, sanitizeSchema],
  // KaTeX runs LAST, AFTER sanitize: remark-math emits <code class="language-math">,
  // a `language-*` class the default sanitize schema already allows, so KaTeX
  // finds the math after sanitization without widening the MathML allowlist.
  // trust:false blocks \href/\htmlClass etc.; throwOnError:false renders the
  // raw source (in errorColor) instead of crashing on malformed input.
  [rehypeKatex, { trust: false, throwOnError: false }],
];

const EMPTY_TAGS: string[] = [];

// The default `.markdown-content ol` padding (1.625em) only fits single-digit
// markers: they render outside the list box in bold DM Sans, so wider numbers
// ("10.", "90.") hang past the message bubble's overflow-hidden edge and get
// their leading digit clipped. Widen the indent by ~one bold digit
// (0.625em) for each extra digit of the largest marker in the list.
function olPaddingLeft(digits: number): string {
  return `${1.625 + (digits - 1) * 0.625}em`;
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isOutgoingMessage?: boolean;
  tags?: string[];
}

/**
 * Renders message content as markdown.
 * Old HTML messages are converted to markdown on the fly via toMarkdown().
 * Supports: bold, italic, strikethrough, links, code, blockquotes, lists, ||spoiler||, @mentions.
 */
export default function MarkdownRenderer({
  content,
  className,
  isOutgoingMessage = false,
  tags = EMPTY_TAGS,
}: MarkdownRendererProps) {
  const navigate = useNavigate();
  // Legacy HTML messages are normalized to markdown via htmlToMarkdown (Turndown);
  // native-markdown messages pass through untouched. preprocessMath then converts
  // the agent's \(...\) / \[...\] LaTeX into remark-math syntax (code-aware) — but
  // ONLY for native markdown. Turndown backslash-escapes literal brackets
  // (`[` → `\[`), which are indistinguishable from display-math delimiters; legacy
  // HTML predates the LaTeX feature and never holds real math, so we skip
  // conversion there to avoid rendering e.g. `[link]` / JSON arrays as KaTeX.
  const markdown = useMemo(() => {
    const isHtml = isHtmlContent(content);
    const normalized = isHtml ? htmlToMarkdown(content) : content;
    return preprocessMath(normalized, !isHtml);
  }, [content]);

  const linkClass = useMemo(
    () =>
      isOutgoingMessage
        ? LINK_CLASS.replaceAll("text-foreground", "text-primary-foreground")
        : LINK_CLASS,
    [isOutgoingMessage],
  );

  const remarkPlugins = useMemo<PluggableList>(
    () => [remarkGfm, remarkMath, remarkSpoiler, [remarkMention, { tags }]],
    [tags],
  );

  const handleSpoilerClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.classList.toggle("revealed");
  }, []);

  const components: Components = useMemo(
    () => ({
      ol: ({ children, node, start, ...props }) => {
        const itemCount =
          node?.children.filter(
            (child) => child.type === "element" && child.tagName === "li",
          ).length ?? 0;
        // Raw-HTML lists (rehypeRaw) deliver `start` as a string — coerce so
        // "90" + 1 doesn't concatenate to "901" and over-pad.
        const parsedStart = Number(start ?? 1);
        const firstMarker = Number.isFinite(parsedStart) ? parsedStart : 1;
        const maxMarker = firstMarker + Math.max(itemCount - 1, 0);
        const digits = String(maxMarker).length;
        return (
          <ol
            start={start}
            style={
              digits > 1 ? { paddingLeft: olPaddingLeft(digits) } : undefined
            }
            {...props}
          >
            {children}
          </ol>
        );
      },
      a: ({ href, children, node: _node, ...props }) => {
        const isInternalRoute =
          typeof href === "string" && /^\/(?!\/)/.test(href);

        if (isInternalRoute) {
          return (
            <a
              href={href}
              className={linkClass}
              onClick={(e) => {
                if (
                  e.metaKey ||
                  e.ctrlKey ||
                  e.shiftKey ||
                  e.altKey ||
                  e.button !== 0
                ) {
                  return;
                }
                e.preventDefault();
                navigate(href);
              }}
              {...props}
            >
              {children}
            </a>
          );
        }

        const isExternalWeb =
          typeof href === "string" && /^(https?:)?\/\//.test(href);

        return (
          <a
            href={href}
            target={isExternalWeb ? "_blank" : undefined}
            rel={isExternalWeb ? "noopener noreferrer" : undefined}
            className={linkClass}
            {...props}
          >
            {children}
          </a>
        );
      },
      span: (props: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
        const {
          children,
          className: propsClassName,
          onClick: propsOnClick,
          node: _node,
          ...rest
        } = props;

        const isMention =
          typeof propsClassName === "string" &&
          propsClassName.includes("mention");

        if (isMention) {
          const username = (rest as Record<string, string>)["data-username"];
          if (username && tags.includes(username)) {
            return (
              <UserProfileDialog username={username}>
                <button
                  type="button"
                  className={cn(
                    "rounded inline cursor-pointer border-none px-1 py-0.5 font-semibold",
                    isOutgoingMessage
                      ? "bg-white/15 text-primary-foreground hover:bg-white/25"
                      : "bg-primary/15 text-primary hover:bg-primary/25",
                  )}
                >
                  {children}
                </button>
              </UserProfileDialog>
            );
          }
        }

        const isSpoiler =
          typeof propsClassName === "string" &&
          propsClassName.includes("spoiler");

        if (!isSpoiler) {
          return (
            <span className={propsClassName} {...rest}>
              {children}
            </span>
          );
        }

        const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
          handleSpoilerClick(event);
          if (typeof propsOnClick === "function") {
            propsOnClick(event);
          }
        };

        return (
          <span
            className={cn("spoiler", propsClassName)}
            data-spoiler="true"
            onClick={handleClick}
            {...rest}
          >
            {children}
          </span>
        );
      },
    }),
    [linkClass, handleSpoilerClick, navigate, tags, isOutgoingMessage],
  );

  return (
    // `translate="no"` + the `notranslate` class opt this subtree out of
    // Google Translate (and similar) DOM rewriting. Those extensions reparent/
    // replace the text nodes React rendered, which desyncs React's virtual DOM
    // and makes the reconciler throw `NotFoundError: Failed to execute
    // 'insertBefore'...` on the next render (csov/ow2-chat-fe#914, Chrome-only).
    // Opting out is the standard mitigation for the Translate-vs-React crash.
    <div
      className={cn("markdown-content notranslate", className)}
      translate="no"
    >
      <Markdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
      >
        {markdown}
      </Markdown>
    </div>
  );
}
